import { useState } from 'react';
import { App, Button, Form, Input } from 'antd';
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppAuth } from '../../api/appAuth';
import Logo from '../../components/Logo/Logo';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import '../Login/Auth.css';

type RegisterForm = {
    displayName: string;
    email: string;
    password: string;
    confirmPassword: string;
};

type OtpForm = {
    otp: string;
};

type AuthApiPayload = {
    success?: boolean;
    message?: string;
};

function isSuccessResponse(response: { status: number; data?: AuthApiPayload }) {
    const httpOk = response.status === 200 || response.status === 201;
    return httpOk && response.data?.success !== false;
}

function getApiErrorMessage(error: unknown, fallback: string) {
    if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message
    ) {
        return error.message;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return fallback;
}

function Register() {
    const { t } = useTranslation('auth');
    const { notification } = App.useApp();
    const navigate = useNavigate();
    const [accountForm] = Form.useForm<RegisterForm>();
    const [otpForm] = Form.useForm<OtpForm>();
    const [step, setStep] = useState<'account' | 'otp'>('account');
    const [pendingAccount, setPendingAccount] = useState<RegisterForm | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const onCreateAccount = async (values: RegisterForm) => {
        setSubmitting(true);

        try {
            const response = await AppAuth.UserRegister({
                displayName: values.displayName.trim(),
                email: values.email.trim(),
                password: values.password,
            });

            console.log("response", response);

            if (!isSuccessResponse(response)) {
                notification.error({
                    message: t('errors.createAccount'),
                    description: getApiErrorMessage(response.data, t('errors.createAccount')),
                });
                return;
            }

            setPendingAccount(values);
            setStep('otp');
            otpForm.resetFields();
            notification.success({
                message: t('otp.sent'),
                description: t('otp.sentTo', { email: values.email.trim() }),
            });
        } catch (error) {
            console.log("error", error);
            notification.error({
                message: t('errors.createAccount'),
                description: getApiErrorMessage(error, t('errors.createAccount')),
            });
        } finally {
            setSubmitting(false);
        }
    };

    const onVerifyOtp = async ({ otp }: OtpForm) => {
        if (!pendingAccount) {
            setStep('account');
            return;
        }

        setSubmitting(true);

        try {
            const response = await AppAuth.SendOtpRegister({
                email: pendingAccount.email.trim(),
                otp,
            });

            console.log("response OTP", response);

            if (!isSuccessResponse(response)) {
                notification.error({
                    message: t('errors.verifyOtp'),
                    description: getApiErrorMessage(response.data, t('errors.verifyOtp')),
                });
                return;
            }

            notification.success({
                message: t('register.success'),
                description: t('register.continue'),
            });
            navigate('/login', { replace: true });
        } catch (error) {
            notification.error({
                message: t('errors.invalidOtp'),
                description: getApiErrorMessage(error, t('errors.correctOtp')),
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="auth">
            <div className="auth__card">
                <div className="auth__language">
                    <LanguageSwitcher compact />
                </div>
                <div className="auth__brand">
                    <Logo />
                </div>

                {step === 'account' ? (
                    <>
                        <h1 className="auth__title">{t('register.title')}</h1>
                        <p className="auth__subtitle">{t('register.subtitle')}</p>

                        <Form
                            form={accountForm}
                            layout="vertical"
                            onFinish={onCreateAccount}
                            initialValues={pendingAccount || undefined}
                        >
                            <Form.Item
                                name="displayName"
                                label={t('fields.displayName')}
                                rules={[
                                    { required: true, message: t('validation.displayNameRequired') },
                                    { min: 3, message: t('validation.displayNameMin') },
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder={t('fields.displayName')}
                                    autoComplete="name"
                                />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label={t('fields.email')}
                                rules={[
                                    { required: true, message: t('validation.emailRequired') },
                                    { type: 'email', message: t('validation.emailInvalid') },
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder={t('fields.email')}
                                    autoComplete="email"
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label={t('fields.password')}
                                rules={[
                                    { required: true, message: t('validation.passwordRequired') },
                                    { min: 6, message: t('validation.passwordMin') },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder={t('fields.password')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label={t('fields.confirmPassword')}
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: t('validation.confirmPassword') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                new Error(t('validation.passwordMismatch'))
                                            );
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder={t('fields.confirmPassword')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {t('register.submit')}
                            </Button>
                        </Form>
                    </>
                ) : (
                    <>
                        <h1 className="auth__title">{t('otp.title')}</h1>
                        <p className="auth__subtitle">
                            {t('otp.verifyEmail', { email: pendingAccount?.email })}
                        </p>

                        <Form form={otpForm} layout="vertical" onFinish={onVerifyOtp}>
                            <Form.Item
                                name="otp"
                                className="auth__otp-item"
                                rules={[
                                    { required: true, message: t('validation.otpRequired') },
                                    { len: 6, message: t('validation.otpLength') },
                                ]}
                            >
                                <Input.OTP
                                    length={6}
                                    size="large"
                                    type="text"
                                    inputMode="numeric"
                                    formatter={(value) => value.replace(/\D/g, '')}
                                    disabled={submitting}
                                    onChange={(value) => {
                                        if (value.length === 6 && !submitting) {
                                            otpForm.setFieldValue('otp', value);
                                            otpForm.submit();
                                        }
                                    }}
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {t('otp.verify')}
                            </Button>
                        </Form>

                        <button
                            type="button"
                            className="auth__back"
                            disabled={submitting}
                            onClick={() => setStep('account')}
                        >
                            {t('register.back')}
                        </button>
                    </>
                )}

                <p className="auth__footer">
                    {t('register.hasAccount')} <Link to="/login">{t('login.submit')}</Link>
                </p>
            </div>
        </div>
    );
}

export default Register;
